import DateFnsUtils from '@date-io/date-fns';
import React, { useContext, useState } from 'react';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import ArrowLeft from '@material-ui/icons/ArrowBack';
import ArrowRight from '@material-ui/icons/ArrowForward';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { DatePicker } from '@material-ui/pickers/DatePicker';
import MuiPickersUtilsProvider from '@material-ui/pickers/MuiPickersUtilsProvider';
import {
  ApiFilterOperation,
  NavigationContext,
  UserSettingsContext,
} from '@eas/common-web';
import { useColumns } from '@modules/es-file-court/all/court-file-all-columns';
import {
  COURT_FILES_ALL_IDENTIFIER,
  COURT_FILES_ALL_VERSION,
} from '@modules/es-file-court/all/court-files-all';
import { EvidenceBrowserUrl } from '@enums';
import { DashboardBarChart } from '../bar-chart';
import { DashboardCard } from '../card';
import { useCurrentAssistantEsFilesStatistics } from '../dashboard-hook';
import { DashboardDivider } from '../divider';
import {
  createInitialFilterState,
  fetchAssistantAutocomplete,
  getFilterStateIndexFromColumnsByKey,
  resolvedEssRecordStates,
  unresolvedEssRecordStates,
} from './utils';

const useStyles = makeStyles({
  chart: {
    marginTop: 24,
  },
});

export function AssistantMyEsFilesStatistics() {
  const classes = useStyles();
  const { navigate } = useContext(NavigationContext);
  const { getTableSettings, setTableSettings } =
    useContext(UserSettingsContext);
  const [year, setYear] = useState(new Date().getFullYear());
  const [allYears, setAllYears] = useState(true);
  const [open, setOpen] = useState(false);
  const { formattedData, loading } = useCurrentAssistantEsFilesStatistics(
    allYears ? undefined : year
  );
  const columns = useColumns();
  const selected = formattedData?.data?.[0];

  const openFiles = async (bar: { state: string; type: string }) => {
    if (loading || !selected?.assistantId) return;
    const requiredFilters = [
      'assistantUserRoles.id',
      'type.id',
      'essRecord.state.id',
      ...(!allYears ? ['number.year'] : []),
    ];
    if (
      requiredFilters.some(
        (field) => getFilterStateIndexFromColumnsByKey(columns, field) < 0
      )
    ) {
      return;
    }
    const settingsId = `${COURT_FILES_ALL_IDENTIFIER}_TABLE`;
    const settings = getTableSettings(settingsId, COURT_FILES_ALL_VERSION);
    const filtersState = createInitialFilterState(columns);
    const setFilter = (field: string, value: any, object?: any) => {
      const index = getFilterStateIndexFromColumnsByKey(columns, field);
      if (index >= 0)
        filtersState[index] = {
          enabled: true,
          value,
          operation: ApiFilterOperation.EQ,
          object,
        };
    };
    setFilter(
      'assistantUserRoles.id',
      selected.assistantId,
      await fetchAssistantAutocomplete({
        id: selected.assistantId,
        name: selected.assistant,
      })
    );
    setFilter('type.id', bar.type);

    const states =
      bar.state === 'RESOLVED'
        ? resolvedEssRecordStates
        : unresolvedEssRecordStates;
    const stateIndex = getFilterStateIndexFromColumnsByKey(
      columns,
      'essRecord.state.id'
    );
    if (stateIndex >= 0) {
      filtersState[stateIndex] = {
        enabled: true,
        value: null,
        operation: ApiFilterOperation.OR,
        filters: states.map((value) => ({
          field: 'essRecord.state.id',
          operation: ApiFilterOperation.EQ,
          value,
        })),
        selectFilter: states,
        object: null,
        numberFilter: null,
      };
    }

    if (!allYears) {
      const yearIndex = getFilterStateIndexFromColumnsByKey(
        columns,
        'number.year'
      );
      if (yearIndex >= 0) {
        filtersState[yearIndex] = {
          enabled: true,
          value: null,
          operation: ApiFilterOperation.AND,
          filters: [
            {
              field: 'number.year',
              operation: ApiFilterOperation.GTE,
              value: year,
            },
            {
              field: 'number.year',
              operation: ApiFilterOperation.LTE,
              value: year,
            },
          ],
          numberFilter: { from: year, to: year },
          object: null,
        };
      }
    }
    setTableSettings(settingsId, {
      ...settings,
      version: COURT_FILES_ALL_VERSION,
      searchQuery: '',
      filtersState,
    });
    navigate(EvidenceBrowserUrl.COURT_FILES_ALL);
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Grid item xs={12}>
        <DashboardCard>
          <Grid container alignItems="center" justify="center">
            <DatePicker
              views={['year']}
              value={new Date(year, 0, 1)}
              maxDate={new Date()}
              minDate={new Date(1990, 0, 1)}
              open={allYears ? false : open}
              onClose={() => setOpen(false)}
              onChange={(date) => date && setYear(date.getFullYear())}
              TextFieldComponent={() => (
                <Button
                  variant="outlined"
                  disabled={allYears}
                  onClick={() => !allYears && setOpen(true)}
                  endIcon={<CalendarTodayIcon fontSize="small" />}
                >
                  {year}
                </Button>
              )}
            />
            <Button
              onClick={() => setYear((value) => value - 1)}
              disabled={allYears || year <= 1990}
            >
              <ArrowLeft />
            </Button>
            <Button
              onClick={() => setYear((value) => value + 1)}
              disabled={allYears || year >= new Date().getFullYear()}
            >
              <ArrowRight />
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={allYears}
                  color="primary"
                  size="small"
                  onChange={(event) => setAllYears(event.target.checked)}
                />
              }
              label="Všechny roky"
            />
          </Grid>
          {loading ? (
            <Typography>Načítání…</Typography>
          ) : !selected || selected.totalCount === 0 ? (
            <Typography>Žádné spisy</Typography>
          ) : (
            <Grid container className={classes.chart}>
              <Grid item xs={12}>
                <DashboardDivider
                  text={`Moje soudní spisy - ${
                    allYears ? 'všechny roky' : year
                  }`}
                />
                <DashboardBarChart
                  data={[
                    ...(selected?.unresolved ?? []),
                    ...(selected?.resolved ?? []),
                  ]}
                  onBarClick={(bar) => openFiles(bar as any)}
                />
              </Grid>
            </Grid>
          )}
        </DashboardCard>
      </Grid>
    </MuiPickersUtilsProvider>
  );
}
